import { BLEND_MODES, Container, IDestroyOptions, Matrix, Texture } from "pixi.js"
import { SoftenedGraphics } from "./softened_graphics"
import { BakedGroups } from "./baked_groups"
import { groupPolygons } from "./building_shadow"
import { shadowBakeResolution } from "./device_tier"
import type { ShadowPolygon } from "./building_shadow_geometry"

/**
 * A soft dark spill round raised things -- the shade a surface keeps where something stands up close
 * against it (ambient occlusion, seen from above). A merlon darkens the roof behind it and the walk at its
 * foot, so it stands off the roof instead of being pasted on it.
 *
 * The caller places the layer UNDER the art it is cut from: only the spill past the art's own outline
 * shows. Each piece is drawn black (its texture's alpha, or a rectangle) and grown by `spread` px (stamped
 * at that distance all round: a blurred outline alone keeps under a quarter of its dark past the edge),
 * softened and baked once per change, and lands MULTIPLIED, so the layer's alpha is how dark it is there.
 */
/**
 * How dark each spill is next to its art at full daylight, and how far it softens (live: window.buildingShadow
 * .halo / .roofHalo). The walls' halo sits just above the sun's shade, under every sprite of its storey.
 */
export const HALO = { wall: { alpha: 0.7, softness: 4, spread: 3 } }
export const WALL_HALO_Z_INDEX = 1.5

export interface HaloPiece {
    /** Art drawn black through its own alpha, at (x, y) in the layer's pixels; or, without one, the rectangle. */
    texture?: Texture
    x: number
    y: number
    width: number
    height: number
}

const keyOf = (pieces: readonly HaloPiece[]): string => {
    let a = 0x811c9dc5, b = 0x9e3779b9
    const mix = (v: number) => {
        a = Math.imul(a ^ v, 0x01000193)
        b = Math.imul(b ^ (v + 0x632be5ab), 0x85ebca6b) ^ (b >>> 13)
    }
    for (const { texture, x, y, width, height } of pieces) {
        mix(texture ? (texture.baseTexture as any).uid ?? 1 : 0)
        mix(texture ? texture.frame.x * 4096 + texture.frame.y : 0)
        mix(Math.round(x)); mix(Math.round(y)); mix(Math.round(width)); mix(Math.round(height))
    }
    return `${pieces.length}:${a >>> 0}:${b >>> 0}`
}

export class SilhouetteHaloLayer extends Container {
    private baked = new BakedGroups<{ key: string; graphics: SoftenedGraphics }>(this)

    constructor(private readonly resolution?: number) {
        super()
        this.interactive = false
        this.interactiveChildren = false
    }

    /** Re-bakes only the groups (pieces near one another) whose pieces changed. */
    draw(pieces: readonly HaloPiece[], softness: number, spread = 0): void {
        const previous = new Map(this.baked.groups.map(group => [group.key, group]))
        const next: { key: string; graphics: SoftenedGraphics }[] = []
        const fresh: SoftenedGraphics[] = []
        const rects = new Map<ShadowPolygon, HaloPiece>(pieces.map(piece => {
            const { x, y, width: w, height: h } = piece
            return [[x, y, x + w, y, x + w, y + h, x, y + h], piece]
        }))
        const resolution = this.resolution ?? shadowBakeResolution()
        groupPolygons([...rects.keys()], softness * 4 + spread * 2).forEach(members => {
            const group = members.map(rect => rects.get(rect))
            const key = `${softness}|${spread}|${resolution}|${keyOf(group)}`
            const kept = previous.get(key)
            if (kept) {
                previous.delete(key)
                next.push(kept)
                return
            }
            const graphics = new SoftenedGraphics(softness, 2, BLEND_MODES.MULTIPLY, resolution)
            const plain = group.filter(piece => !piece.texture)
            if (plain.length) {
                graphics.beginFill(0x000000, 1)
                plain.forEach(({ x, y, width, height }) =>
                    graphics.drawRect(x - spread, y - spread, width + 2 * spread, height + 2 * spread))
                graphics.endFill()
            }
            const offsets = spread > 0 ? [[0, 0], [spread, 0], [-spread, 0], [0, spread], [0, -spread],
                [spread * 0.7, spread * 0.7], [-spread * 0.7, spread * 0.7], [spread * 0.7, -spread * 0.7], [-spread * 0.7, -spread * 0.7]] : [[0, 0]]
            group.forEach(piece => {
                if (!piece.texture) return
                offsets.forEach(([dx, dy]) => graphics.stamp(piece.texture, new Matrix(1, 0, 0, 1, piece.x + dx, piece.y + dy), 0x000000))
            })
            next.push({ key, graphics })
            fresh.push(graphics)
        })
        this.baked.replace(next, fresh)
    }

    get groupCount(): number { return this.baked.groups.length }

    destroy(options?: IDestroyOptions | boolean): void {
        this.baked.clear()
        super.destroy(options)
    }
}
